import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Modal,
  TextField,
  Popover,
  CircularProgress,
} from "@mui/material";
import {
  TrendingUp,
  Shield,
  Work,
  CheckCircle,
  ExpandMore,
  Mic,
  Close,
} from "@mui/icons-material";
import Navbar from "../../components/NavigationMenu/NavBar";
import Footer from "../../components/Footer/Footer";
import Vapi from "@vapi-ai/web";
import CalendarModal from "../../components/CalendarModal/CalendarModal";
const vapi = new Vapi(process.env.REACT_APP_VAPI_PUBLIC_KEY);

// ====== IMPORTANT: set your current Web App URL here (Deploy -> Web app -> Anyone) ======
const GAS_URL =
  "https://script.google.com/macros/s/AKfycbx2fKm1EpiWyfaX__ja7al7ICSjhtdZTK1b7FJM10t77alEDT5DqnWnNK4MLS_kvt4t/exec";

// ---------- tiny JSONP utility (no CORS needed) ----------
function jsonp(url, params = {}, timeoutMs = 12000) {
  return new Promise((resolve, reject) => {
    const cbName = "cb_" + Math.random().toString(36).slice(2);
    const script = document.createElement("script");
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error("JSONP timeout"));
    }, timeoutMs);

    function cleanup() {
      clearTimeout(timeout);
      delete window[cbName];
      if (script.parentNode) script.parentNode.removeChild(script);
    }

    window[cbName] = (data) => {
      cleanup();
      resolve(data);
    };

    const qs = new URLSearchParams({ ...params, callback: cbName }).toString();
    script.src = `${url}?${qs}`;
    script.onerror = () => {
      cleanup();
      reject(new Error("JSONP network error"));
    };
    document.head.appendChild(script);
  });
}

// ---------- Google Sheets service (JSONP) ----------
const GoogleSheetsService = {
  async saveUserData(userData) {
    const params = {
      action: "saveUserData",
      email: userData.email,
      phoneNumber: "", // Remove phone number data
      assistantName: userData.assistantName || "Unknown Assistant",
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      referrer: typeof document !== "undefined" ? document.referrer || "" : "",
      timestamp: new Date().toISOString(),
    };
    // JSONP returns a real object (not an opaque response), so you can branch on success
    const result = await jsonp(GAS_URL, params);
    if (result?.success === false) {
      throw new Error(result?.error || result?.message || "Submission failed");
    }
    return result;
  },

  async testConnection() {
    return jsonp(GAS_URL, { action: "ping" });
  },
};

// ------------------- User Details Popup -------------------
const UserDetailsPopup = ({ open, anchorEl, onClose, onSubmit, loading }) => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setEmailError("");

    if (!email.trim()) {
      setEmailError("Email is required");
      return;
    }
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email");
      return;
    }

    onSubmit({
      email: email.trim(),
    });
  };

  const handleClose = () => {
    setEmail("");
    setEmailError("");
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(10px)",
            maxWidth: "340px",
            width: "100%",
          },
        },
      }}
    >
      <Paper
        sx={{
          p: 3,
          background:
            "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)",
          borderRadius: "16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "#333", fontSize: "1.1rem" }}
          >
            Quick Details
          </Typography>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              minWidth: "auto",
              p: 0.5,
              color: "#666",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
            }}
          >
            <Close sx={{ fontSize: 20 }} />
          </Button>
        </Box>

        <Typography
          variant="body2"
          sx={{ color: "#666", mb: 2.5, fontSize: "0.875rem", lineHeight: 1.4 }}
        >
          Enter your email to start chatting with our AI agent
        </Typography>

        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 2.5 }}>
            <TextField
              fullWidth
              type="email"
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!emailError}
              helperText={emailError}
              size="small"
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  "& fieldset": { borderColor: "rgba(0, 0, 0, 0.23)" },
                  "&:hover fieldset": { borderColor: "#ea6347" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#ea6347",
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputBase-input": { fontSize: "0.875rem" },
                "& .MuiInputLabel-root": { fontSize: "0.875rem" },
              }}
            />
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: "#ea6347",
              "&:hover": { backgroundColor: "#d55640" },
              "&:disabled": { backgroundColor: "rgba(234, 99, 71, 0.6)" },
              py: 1.2,
              fontSize: "0.9rem",
              fontWeight: 600,
              borderRadius: "8px",
              textTransform: "none",
              boxShadow: "0 4px 12px rgba(234, 99, 71, 0.3)",
              "&:hover": { boxShadow: "0 6px 16px rgba(234, 99, 71, 0.4)" },
              position: "relative",
            }}
          >
            {loading && (
              <CircularProgress
                size={20}
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  marginLeft: "-10px",
                  marginTop: "-10px",
                  color: "white",
                }}
              />
            )}
            <span style={{ opacity: loading ? 0 : 1 }}>
              {loading ? "Starting..." : "Start Conversation"}
            </span>
          </Button>
        </form>
      </Paper>
    </Popover>
  );
};



const styles = {
  header: {
    position: "fixed",
    top: 0,
    width: "100%",
    background: "rgba(255, 255, 255, 0.98)",
    backdropFilter: "blur(10px)",
    zIndex: 1000,
    transition: "all 0.3s ease",
    boxShadow: "0 2px 20px rgba(0, 0, 0, 0.1)",
  },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 0px",
  },
  nav: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  voiceAgentCircle: {
    position: "relative",
    margin: "0 auto",
    width: "120px",
    height: "120px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #f09481, #ea6347)",
    padding: "3px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 20px rgba(226, 107, 74, 0.3)",
  },
  voiceAgentCircleActive: {
    animation: "pulse 2s infinite",
    boxShadow: "0 8px 40px rgba(226, 107, 74, 0.5)",
    transform: "scale(1.05)",
  },
  voiceAgentInner: {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    background: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid rgba(0, 0, 0, 0.1)",
  },
  voiceAgentIcon: {
    fontSize: "50px",
    color: "#ea6347",
    zIndex: 2,
  },
  voiceAgentPulse: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "50px",
    height: "50px",
    border: "3px solid #ea6347",
    borderRadius: "50%",
    animation: "ping 2s infinite",
  },
  rightSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "24px",
    minWidth: "320px",
    padding: "32px",
    background: "rgba(255, 255, 255, 0.95)",
    borderRadius: "20px",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    boxShadow: "0 3px 12px rgba(0, 0, 0, 0.06)",
  },
};

const NewLandingPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [userDetailsPopupOpen, setUserDetailsPopupOpen] = useState(false);
  const [micButtonRef, setMicButtonRef] = useState(null);
  const [isSubmittingUserDetails, setIsSubmittingUserDetails] = useState(false);
  const [userDetailsSubmitted, setUserDetailsSubmitted] = useState(false);
  const [submittedUserData, setSubmittedUserData] = useState(null);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleVoiceToggle = async (event) => {
    if (!userDetailsSubmitted && !isVoiceActive) {
      setMicButtonRef(event.currentTarget);
      setUserDetailsPopupOpen(true);
      return;
    }
    if (!isVoiceActive) {
      try {
        vapi.start(process.env.REACT_APP_VAPI_SALES_ASSISTANT_ID);
        setIsVoiceActive(true);
      } catch (error) {
        console.error("Error starting Vapi assistant:", error);
        alert(
          "Failed to start voice interaction. Please check microphone permissions."
        );
      }
    } else {
      try {
        vapi.stop();
        setIsVoiceActive(false);
      } catch (error) {
        console.error("Error stopping Vapi assistant:", error);
      }
    }
  };

  const handleUserDetailsSubmit = async (userDetails) => {
    setIsSubmittingUserDetails(true);
    try {
      const result = await GoogleSheetsService.saveUserData({
        ...userDetails,
        assistantName: "Shopify Services Agent",
      });

      setSubmittedUserData(userDetails);
      setUserDetailsSubmitted(true);
      setUserDetailsPopupOpen(false);

      try {
        vapi.start("YOUR_ASSISTANT_ID"); // Replace with actual assistant ID
        setIsVoiceActive(true);
      } catch (error) {
        console.error("Error starting Vapi assistant:", error);
        alert(
          "Details saved, but couldn't start voice. Check microphone permissions and try again."
        );
      }
    } catch (error) {
      console.error("Error submitting user details:", error);
      alert(`Failed to submit details: ${error.message}. Please try again.`);
    } finally {
      setIsSubmittingUserDetails(false);
    }
  };

  const handleCloseUserDetailsPopup = () => {
    setUserDetailsPopupOpen(false);
    setMicButtonRef(null);
  };

  useEffect(() => {
    vapi.on("call-start", () => {
      setIsVoiceActive(true);
    });

    vapi.on("call-end", () => {
      setIsVoiceActive(false);
    });

    vapi.on("error", (error) => {
      console.error("VAPI Error:", error);
      setIsVoiceActive(false);
    });

    return () => {
      vapi.removeAllListeners();
    };
  }, []);

  return (
    <Box component="main">
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              box-shadow: 0 4px 20px rgba(226, 107, 74, 0.3);
            }
            50% {
              transform: scale(1.05);
              box-shadow: 0 8px 40px rgba(226, 107, 74, 0.5);
            }
          }

          @keyframes ping {
            0% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 1;
            }
            75%, 100% {
              transform: translate(-50%, -50%) scale(2);
              opacity: 0;
            }
          }
        `}
      </style>

      {/* Header */}
      <header style={styles.header}>
        <div style={styles.container}>
          <nav style={styles.nav}>
            <Navbar />
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <Box
        sx={{
          pt: 25,
          pb: 15,
          background:
            "radial-gradient(ellipse at top, rgba(226, 107, 74, 0.05) 0%, transparent 70%)",
          position: "relative",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.5rem", sm: "3rem", md: "4rem" },
                  fontWeight: 700,
                  mb: 3,
                  color: "#1a1a1a",
                  lineHeight: 1.2,
                }}
              >
                Add AI Voice Agents to Your Shopify Services
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: "text.secondary",
                  mb: 4,
                  fontWeight: 400,
                  lineHeight: 1.6,
                }}
              >
                Unlock new revenue stream and give your clients 24/7 support —
                without extra work, staff, or tech overhead.
              </Typography>

              <Button
                variant="contained"
                size="large"
                onClick={handleOpenModal}
                sx={{
                  backgroundColor: "#ff5722",
                  py: 2,
                  px: 4,
                  fontSize: "16px",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "50px", // Capsule shape
                  boxShadow: "0 4px 14px rgba(255, 87, 34, 0.25)",
                  "&:hover": {
                    backgroundColor: "#e64a19",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(255, 87, 34, 0.35)",
                  },
                }}
              >
                Talk to Us
              </Button>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <div style={styles.rightSection}>
                  <div
                    style={{
                      ...styles.voiceAgentCircle,
                      ...(isVoiceActive ? styles.voiceAgentCircleActive : {}),
                    }}
                    onClick={handleVoiceToggle}
                  >
                    <div style={styles.voiceAgentInner}>
                      <div style={{ position: "relative" }}>
                        <Mic style={styles.voiceAgentIcon} />
                        {isVoiceActive && (
                          <div style={styles.voiceAgentPulse}></div>
                        )}
                      </div>
                    </div>
                  </div>

                  <Typography
                    sx={{
                      color: "#666",
                      fontSize: "0.875rem",
                      textAlign: "center",
                      fontWeight: "500",
                    }}
                  >
                    {isVoiceActive
                      ? "Click to Stop"
                      : userDetailsSubmitted
                      ? "Try our Agent"
                      : "Talk to the Agent"}
                  </Typography>

                  {userDetailsSubmitted && submittedUserData && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: "rgba(234, 99, 71, 0.1)",
                        borderRadius: "8px",
                        border: "1px solid rgba(234, 99, 71, 0.2)",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#333",
                          fontSize: "0.8rem",
                          textAlign: "center",
                        }}
                      >
                        Connected as: {submittedUserData.email}
                      </Typography>
                    </Box>
                  )}
                </div>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why This Partnership Matters Section */}
      <Box sx={{ py: 10, backgroundColor: "#fafafa" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", md: "3rem" },
              fontWeight: 700,
              mb: 3,
              textAlign: "center",
            }}
          >
            Why This Partnership Matters
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: "text.secondary",
              mb: 6,
              textAlign: "center",
              fontWeight: 400,
              maxWidth: 800,
              mx: "auto",
            }}
          >
            With Beanbag AI Voice Agents, you can offer all this to your clients
            — and grow your agency's revenue at the same time.
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <CheckCircle sx={{ color: "#4caf50", fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Recover more abandoned carts
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <CheckCircle sx={{ color: "#4caf50", fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Provide faster, human-like customer support
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <CheckCircle sx={{ color: "#4caf50", fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Cut down costs of extra human agents
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* What's in it for You Section */}
      <Box sx={{ py: 10, backgroundColor: "white" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", md: "3rem" },
              fontWeight: 700,
              mb: 3,
            }}
          >
            What's in it for You (Agency Owners)
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: "text.secondary",
              mb: 6,
              fontWeight: 400,
              maxWidth: 800,
            }}
          >
            Earn steady income, position your agency as forward-thinking, and
            avoid extra workload with a DIY-ready, branded solution.
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: "100%", p: 2 }}>
                <CardContent>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      backgroundColor: "rgba(255, 87, 34, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <TrendingUp sx={{ color: "#ff5722", fontSize: 28 }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                    New Revenue Stream
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Shopify agencies are turning this into $10K–$20K/client/year
                    in steady recurring revenue.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ height: "100%", p: 2 }}>
                <CardContent>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      backgroundColor: "rgba(255, 87, 34, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <Shield sx={{ color: "#ff5722", fontSize: 28 }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                    Future-Proof Client Relationships
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Position your agency as forward-thinking by introducing AI
                    solutions that save clients money and boost sales.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ height: "100%", p: 2 }}>
                <CardContent>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      backgroundColor: "rgba(255, 87, 34, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <Work sx={{ color: "#ff5722", fontSize: 28 }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                    No Extra Workload
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    You don't need to hire, train, or manage anyone. We provide
                    a DIY-ready solution for your clients, branded through your
                    agency.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Voice Agents vs Traditional Support Section */}
      <Box sx={{ py: 10, backgroundColor: "#fafafa" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", md: "3rem" },
              fontWeight: 700,
              mb: 6,
              textAlign: "center",
            }}
          >
            Voice Agents vs. Traditional Human Support
          </Typography>

          <TableContainer
            component={Paper}
            sx={{ borderRadius: 3, boxShadow: 2 }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: "18px", py: 3 }}>
                    Capability
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "18px", py: 3 }}>
                    Voice Agents (Beanbag AI)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "18px", py: 3 }}>
                    Human Agent Support
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  {
                    capability: "Availability",
                    voice: "24/7, always-on conversations",
                    human: "Limited by shifts",
                  },
                  {
                    capability: "Human-Like Feel",
                    voice: "Natural, empathetic voices",
                    human: "Inconsistent, depends on training & availability",
                  },
                  {
                    capability: "Speed",
                    voice: "Instant responses, no lag",
                    human: "Slower handling, queues",
                  },
                  {
                    capability: "Cost",
                    voice: "$10–$20/hour, no extra staffing",
                    human: "Higher staffing overhead",
                  },
                ].map((row, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontWeight: 500, py: 3 }}>
                      {row.capability}
                    </TableCell>
                    <TableCell sx={{ py: 3 }}>{row.voice}</TableCell>
                    <TableCell sx={{ py: 3 }}>{row.human}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Paper
            sx={{
              backgroundColor: "#fff8e1",
              border: "1px solid #ffcc02",
              p: 3,
              mt: 4,
              textAlign: "center",
              borderRadius: 2,
            }}
          >
            <Typography variant="body1">
              Shopify agencies partnering with Beanbag AI are already seeing{" "}
              <Typography
                component="span"
                sx={{ fontWeight: 700, color: "#ff5722" }}
              >
                $10K–$20K/client/year
              </Typography>{" "}
              in steady recurring revenue.
            </Typography>
          </Paper>
        </Container>
      </Box>

      {/* How the Partnership Works Section */}
      <Box sx={{ py: 10, backgroundColor: "white" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", md: "3rem" },
              fontWeight: 700,
              mb: 6,
              textAlign: "center",
            }}
          >
            How the Partnership Works
          </Typography>

          <Grid container spacing={3}>
            {[
              {
                step: "1",
                title: "We set up AI voice agents",
                description:
                  "DIY-friendly for your clients, with no technical lift for your team.",
              },
              {
                step: "2",
                title: "You package this as part of services",
                description:
                  "Offer voice agents alongside your Shopify engagements.",
              },
              {
                step: "3",
                title: "You earn ongoing revenue",
                description:
                  "Recurring income every month as clients use voice agents.",
              },
            ].map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    p: 3,
                    height: "60%",
                    boxShadow: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "grey.200",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          backgroundColor: "#ff5722",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          fontSize: "18px",
                          fontWeight: "bold",
                          boxShadow: 1,
                          flexShrink: 0,
                        }}
                      >
                        {item.step}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            fontSize: "1.125rem",
                            lineHeight: 1.3,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: "0.875rem",
                            lineHeight: 1.5,
                          }}
                        >
                          {item.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: "center", mt: 6 }}>
            <Typography
              variant="h6"
              sx={{
                color: "#4caf50",
                fontWeight: 600,
                fontStyle: "italic",
              }}
            >
              Simple, scalable, profitable.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 10, backgroundColor: "#fafafa" }}>
        <Container maxWidth="md">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", md: "3rem" },
              fontWeight: 700,
              mb: 6,
              textAlign: "center",
            }}
          >
            Frequently Asked Questions (FAQs)
          </Typography>

          <Box>
            {[
              {
                question:
                  "Do I need to handle setup or client support for the agents?",
                answer:
                  "No. Beanbag AI manages the technology. You just introduce it as part of your services.",
              },
              {
                question:
                  "Will my clients need to hire staff to manage voice agents?",
                answer:
                  "Not at all. Voice agents run 24/7 without additional manpower.",
              },
              {
                question: "How do I earn revenue from this?",
                answer:
                  "Every client using voice agents generates recurring income for your agency.",
              },
              {
                question: "What if my clients aren't asking for this yet?",
                answer:
                  "Early adopter agencies are already offering voice agents and making steady monthly revenue. You'll be ahead of the curve.",
              },
              {
                question:
                  "Will my clients know this is from Beanbag AI or from my agency?",
                answer:
                  "We offer a partner-friendly setup. You stay the trusted face for your clients while Beanbag AI powers the technology in the background.",
              },
              {
                question: "Do I need technical expertise to offer this?",
                answer:
                  "No. Everything is plug-and-play. We handle the heavy lifting, so you can focus on your clients.",
              },
              {
                question:
                  "What types of Shopify stores benefit most from voice agents?",
                answer:
                  "E-commerce stores with frequent cart abandons, high customer support queries, or international customers see the biggest lift.",
              },
              {
                question: "How fast can my clients go live with voice agents?",
                answer:
                  "In most cases, setup takes less than a week. Clients can start seeing value in a month's time.",
              },
              {
                question: "Can voice agents scale as my clients grow?",
                answer:
                  "Yes. Whether your client has 100 calls or 10,000, the system scales without extra cost or manpower.",
              },
              {
                question: "What languages or regions do voice agents support?",
                answer:
                  "Beanbag AI supports multiple accents and regions, making it easy for your clients to engage global customers.",
              },
              {
                question: "How do I explain the ROI to my clients?",
                answer:
                  "Voice agents recover abandoned carts, close sales, and handle 24/7 support. This reduces staffing costs while increasing conversions - a clear double benefit.",
              },
              {
                question:
                  "Is there any upfront cost for my agency to join the program?",
                answer:
                  "No. You start by introducing it to clients, and you earn from the revenue they generate.",
              },
            ].map((faq, index) => (
              <Accordion key={index} sx={{ mb: 1.5, borderRadius: 2 }}>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    py: 2,
                    "& .MuiAccordionSummary-content": {
                      margin: "12px 0",
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      textAlign: "left",
                    }}
                  >
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.6 }}
                  >
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box sx={{ py: 10, backgroundColor: "white" }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "1.8rem", md: "2.5rem" },
                fontWeight: 700,
                mb: 4,
                lineHeight: 1.3,
              }}
            >
              Help your clients cut costs, recover more sales, and stay
              future-ready — while your agency grows new recurring revenue.
            </Typography>

            <Button
              variant="contained"
              size="large"
              onClick={handleOpenModal}
              sx={{
                backgroundColor: "#ff5722",
                py: 2.5,
                px: 5,
                fontSize: "18px",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "50px", // Capsule shape
                boxShadow: "0 4px 14px rgba(255, 87, 34, 0.25)",
                "&:hover": {
                  backgroundColor: "#e64a19",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(255, 87, 34, 0.35)",
                },
              }}
            >
              Talk to Us
            </Button>
          </Box>
        </Container>
      </Box>

      <div style={{ padding: "20px" }}>
        <Footer />
      </div>

      <CalendarModal open={modalOpen} handleClose={handleCloseModal} />

      <UserDetailsPopup
        open={userDetailsPopupOpen}
        anchorEl={micButtonRef}
        onClose={handleCloseUserDetailsPopup}
        onSubmit={handleUserDetailsSubmit}
        loading={isSubmittingUserDetails}
      />
    </Box>
  );
};

export default NewLandingPage;